<template>
    <div ref="headerEl" class="dt-header" :class="{'dt-header--selectable': selectable}">
        <div v-if="selectable" class="dt-header-cell dt-header-cell--checkbox" @click="onToggleAll">
            <span class="dt-checkbox" :class="{'dt-checkbox--checked': allSelected, 'dt-checkbox--indeterminate': someSelected}">
                <svg v-if="allSelected" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>
                <svg v-else-if="someSelected" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><line x1="5" y1="12" x2="19" y2="12"/></svg>
            </span>
        </div>
        <div v-if="expandable" class="dt-header-cell dt-header-cell--expand"></div>
        <div v-for="col in columns" :key="col.key"
             class="dt-header-cell"
             :class="[col.cellClass, 'dt-align-' + col.align, {'dt-header-cell--sortable': col.sortable, 'dt-header-cell--sorted': sortKey === col.key}]"
             :style="cellStyle(col)"
             @click="col.sortable && onSort(col.key)"
             @contextmenu.prevent="onContextMenu($event)">
            <span class="dt-header-label">{{ col.label }}</span>
            <span v-if="col.sortable" class="dt-sort-icon" :class="{'dt-sort-icon--asc': sortKey === col.key && sortDir === 'asc', 'dt-sort-icon--desc': sortKey === col.key && sortDir === 'desc'}">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M12 5v14"/><path v-if="sortDir === 'desc' && sortKey === col.key" d="M19 12l-7 7-7-7"/><path v-else d="M5 12l7-7 7 7"/></svg>
            </span>
            <span v-if="col.resizable && !isCompact" class="dt-resize-handle" @mousedown.stop="onResizeStart($event, col)" @touchstart.stop.prevent="onResizeStart($event, col)"></span>
        </div>
        <div v-if="hasActions" class="dt-header-cell dt-header-cell--actions"></div>
    </div>
</template>

<script>
export default {
    name: 'DataTableHeader',
    inject: ['panelVm'],
    props: {
        columns: Array,
        selectable: Boolean,
        expandable: Boolean,
        hasActions: Boolean,
        sortKey: String,
        sortDir: String,
        allSelected: Boolean,
        someSelected: Boolean,
        columnWidths: Object,
        isCompact: Boolean,
    },
    emits: ['sort', 'toggle-all', 'resize', 'column-menu'],
    methods: {
        cellStyle(col) {
            const w = this.columnWidths[col.key] || col.width
            if (w) return { width: w + 'px', minWidth: col.minWidth + 'px', flexGrow: 0, flexShrink: 0, flexBasis: w + 'px' }
            return { flex: col.flex ?? 1, minWidth: col.minWidth + 'px' }
        },
        onSort(key) {
            this.$emit('sort', key)
        },
        onContextMenu(e) {
            this.$emit('column-menu', { x: e.clientX, y: e.clientY })
        },
        onToggleAll() {
            this.$emit('toggle-all')
        },
        getAvailableWidth() {
            const header = this.$refs.headerEl
            if (!header) return 800
            const headerW = header.clientWidth
            let fixedW = 0
            if (this.selectable) fixedW += 32
            if (this.expandable) fixedW += 28
            if (this.hasActions) fixedW += 44
            return headerW - fixedW - 24
        },
        onResizeStart(e, col) {
            const p = e.touches ? e.touches[0] : e
            const cell = e.target.closest('.dt-header-cell')
            const startX = p.clientX
            const startW = cell.getBoundingClientRect().width
            const availableWidth = this.getAvailableWidth()

            const fixedTotal = this.columns.reduce((sum, c) => {
                if (c.key === col.key) return sum
                const cw = this.columnWidths[c.key] || c.width
                if (cw) return sum + cw
                return sum
            }, 0)

            const flexCols = this.columns.filter(c => c.key !== col.key && !this.columnWidths[c.key] && !c.width)
            const minFlexTotal = flexCols.reduce((sum, c) => sum + (c.minWidth || 40), 0)
            const maxAllowed = availableWidth - fixedTotal - minFlexTotal

            const onMove = (ev) => {
                const mp = ev.touches ? ev.touches[0] : ev
                ev.preventDefault()
                const dx = mp.clientX - startX
                const newW = Math.max(col.minWidth || 40, Math.min(maxAllowed, Math.round(startW + dx)))
                this.$emit('resize', col.key, newW)
            }

            const onUp = () => {
                document.removeEventListener('mousemove', onMove)
                document.removeEventListener('mouseup', onUp)
                document.removeEventListener('touchmove', onMove)
                document.removeEventListener('touchend', onUp)
            }

            document.addEventListener('mousemove', onMove)
            document.addEventListener('mouseup', onUp)
            document.addEventListener('touchmove', onMove, { passive: false })
            document.addEventListener('touchend', onUp)
        },
    },
}
</script>
